// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Grid, Icon, Stepper, StepButton, Step, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

// Internal Dependencies
import Info from './info';
import Dates from './dates';
import Media from './media';
import PageList from './pageList';
import HighlightList from './highlightList';
import GeographicTarget from './geographicTarget';
import Constants from '../../common/constants';
import { convertTime12to24, convertTime24to12 } from '../../common/util';
import { IsDateRangeOverlap, IsEmpty, IsTimePeriodOverlap } from '../../common/checks';
import { getAdById, updateAd } from '../../actions/admin/advertisementAction';
import apiService from '../../services.js/apiService';

const useStyles = makeStyles(() => ({
  root: {
    width:      '100%',
    padding:    15,
    paddingTop: 20,
  },
}));

// eslint-disable-next-line no-shadow
const Advertisement = ({ match, getAdById, updateAd }) => {
  const stepperClasses = useStyles();
  const [loaded, setLoaded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [goBack, setGoBack] = useState(false);
  const [steps, setSteps] = useState(
    ['Advertisement Source', 'Advertisement Image', 'Geographic Target', 'Dates & Launch'],
  );

  const [adType, setAdType] = useState(null);
  const [adUrl, setAdUrl] = useState('');
  const [postId, setPostId] = useState('');
  const [pageId, setPageId] = useState('');
  const [adImages, setAdImages] = useState({
    display:  null,
    original: null,
  });
  const [adVideo, setAdVideo] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');
  const [startTimeHours, setStartTimeHours] = useState(null);
  const [startTimeMins, setStartTimeMins] = useState(null);
  const [startTimeType, setStartTimeType] = useState(null);
  const [endTimeHours, setEndTimeHours] = useState(null);
  const [endTimeMins, setEndTimeMins] = useState(null);
  const [endTimeType, setEndTimeType] = useState(null);
  const [isSpecificTime, setIsSpecificTime] = useState(false);
  const [isScreenTakeOverChecked, setIsScreenTakeOverChecked] = useState(false);

  async function fetchAdvertisementData() {
    try {
      const ad = await getAdById(match.params.id);
      if (ad) {
        setAdType(ad.type);
        setAdUrl(ad.url);
        setAdImages({
          display:  ad.cropImage || ad.image || null,
          original: ad.image || null,
        });
        setAdVideo(ad.video);
        setPostId(ad.postId);
        setPageId(ad.pageId);
        setDateFrom(ad.dateFrom ? ad.dateFrom : new Date());
        setDateTill(ad.dateTill ? ad.dateTill : new Date());
        setIsSpecificTime(!IsEmpty(ad.dailyStartTime));
        setIsScreenTakeOverChecked(ad.isScreenTakeOver || false);

        // From saved time, E.g: 22:00:00+08:00
        if (!IsEmpty(ad.dailyStartTime)) {
          const dailyStartTime = convertTime24to12(ad.dailyStartTime.substring(0, 5)).split(':');
          setStartTimeHours(dailyStartTime[0]);
          setStartTimeMins(dailyStartTime[1].split(' ')[0]);
          setStartTimeType(dailyStartTime[1].split(' ')[1]);
        }
        if (!IsEmpty(ad.dailyEndTime)) {
          const dailyEndTime = convertTime24to12(ad.dailyEndTime.substring(0, 5)).split(':');
          setEndTimeHours(dailyEndTime[0]);
          setEndTimeMins(dailyEndTime[1].split(' ')[0]);
          setEndTimeType(dailyEndTime[1].split(' ')[1]);
        }

        setLoaded(true);

        if (ad.type !== Constants.AdType.Post
          && ad.type !== Constants.AdType.Url
          && ad.type !== Constants.AdType.Page
          && ad.type !== Constants.AdType.Highlight) {
          setSteps(['Advertisement Image/Video', 'Geographic Target', 'Dates & Launch']);
        } else if (ad.type === Constants.AdType.Url) {
          setSteps(['Advertisement Source', 'Advertisement Image/Video', 'Geographic Target', 'Dates & Launch']);
        }
      }
    } catch (err) {
      toast.error(err.message);
      setGoBack(true);
    }
  }

  const onChangeSpecificTime = () => {
    setIsSpecificTime(!isSpecificTime);

    if (isSpecificTime) {
      setStartTimeHours(null);
      setStartTimeMins(null);
      setStartTimeType(null);
      setEndTimeHours(null);
      setEndTimeMins(null);
      setEndTimeType(null);
    }
  };

  useEffect(() => {
    fetchAdvertisementData();
  }, []);

  function getStepContent(step) {
    if (adType !== Constants.AdType.Post
        && adType !== Constants.AdType.Url
        && adType !== Constants.AdType.Page
        && adType !== Constants.AdType.Highlight) {
      switch (step) {
        case 0:
          return (
            <Media
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              postId={postId}
              adId={match.params.id}
              images={adImages}
              adVideo={adVideo}
              adType={adType}
              onChangedImages={(e) => setAdImages(e)}
              onChangedVideo={(e) => setAdVideo(e)}
            />
          );
        case 1:
          return (
            <GeographicTarget
              adId={match.params.id}
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
            />
          );
        case 2:
          return (
            <Dates
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              dateFrom={dateFrom}
              dateTill={dateTill}
              onChangedDateFrom={(e) => setDateFrom(e)}
              onChangedDateTill={(e) => setDateTill(e)}
              onUpdate={handleUpdate}
              startTimeHours={startTimeHours}
              startTimeMins={startTimeMins}
              startTimeType={startTimeType}
              endTimeHours={endTimeHours}
              endTimeMins={endTimeMins}
              endTimeType={endTimeType}
              setStartTimeHours={setStartTimeHours}
              setStartTimeMins={setStartTimeMins}
              setStartTimeType={setStartTimeType}
              setEndTimeHours={setEndTimeHours}
              setEndTimeMins={setEndTimeMins}
              setEndTimeType={setEndTimeType}
              isSpecificTime={isSpecificTime}
              onChangeSpecificTime={onChangeSpecificTime}
              isScreenTakeOverChecked={isScreenTakeOverChecked}
              setIsScreenTakeOverChecked={setIsScreenTakeOverChecked}
            />
          );
        default:
      }
    } else if (adType === Constants.AdType.Page) {
      switch (step) {
        case 0:
          return (
            <PageList
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              adType={adType}
              adUrl={adUrl}
              pageId={pageId}
              onChangedAdUrl={(e) => setAdUrl(e)}
              onChangedPageId={(e) => setPageId(e)}
            />
          );
        case 1:
          return (
            <Media
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              pageId={pageId}
              adId={match.params.id}
              images={adImages}
              adVideo={adVideo}
              adType={adType}
              onChangedImages={(e) => setAdImages(e)}
              onChangedVideo={(e) => setAdVideo(e)}
            />
          );
        case 2:
          return (
            <GeographicTarget
              adId={match.params.id}
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
            />
          );
        case 3:
          return (
            <Dates
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              dateFrom={dateFrom}
              dateTill={dateTill}
              onChangedDateFrom={(e) => setDateFrom(e)}
              onChangedDateTill={(e) => setDateTill(e)}
              onUpdate={handleUpdate}
              startTimeHours={startTimeHours}
              startTimeMins={startTimeMins}
              startTimeType={startTimeType}
              endTimeHours={endTimeHours}
              endTimeMins={endTimeMins}
              endTimeType={endTimeType}
              setStartTimeHours={setStartTimeHours}
              setStartTimeMins={setStartTimeMins}
              setStartTimeType={setStartTimeType}
              setEndTimeHours={setEndTimeHours}
              setEndTimeMins={setEndTimeMins}
              setEndTimeType={setEndTimeType}
              isSpecificTime={isSpecificTime}
              onChangeSpecificTime={onChangeSpecificTime}
              isScreenTakeOverChecked={isScreenTakeOverChecked}
              setIsScreenTakeOverChecked={setIsScreenTakeOverChecked}
            />
          );
        default: break;
      }
    } else if (adType === Constants.AdType.Highlight) {
      switch (step) {
        case 0:
          return (
            <HighlightList
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              adType={adType}
              adUrl={adUrl}
              pageId={pageId}
              onChangedAdUrl={(e) => setAdUrl(e)}
              onChangedPageId={(e) => setPageId(e)}
            />
          );
        case 1:
          return (
            <Media
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              pageId={pageId}
              adId={match.params.id}
              images={adImages}
              adVideo={adVideo}
              adType={adType}
              onChangedImages={(e) => setAdImages(e)}
              onChangedVideo={(e) => setAdVideo(e)}
            />
          );
        case 2:
          return (
            <GeographicTarget
              adId={match.params.id}
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
            />
          );
        case 3:
          return (
            <Dates
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              dateFrom={dateFrom}
              dateTill={dateTill}
              onChangedDateFrom={(e) => setDateFrom(e)}
              onChangedDateTill={(e) => setDateTill(e)}
              onUpdate={handleUpdate}
              startTimeHours={startTimeHours}
              startTimeMins={startTimeMins}
              startTimeType={startTimeType}
              endTimeHours={endTimeHours}
              endTimeMins={endTimeMins}
              endTimeType={endTimeType}
              setStartTimeHours={setStartTimeHours}
              setStartTimeMins={setStartTimeMins}
              setStartTimeType={setStartTimeType}
              setEndTimeHours={setEndTimeHours}
              setEndTimeMins={setEndTimeMins}
              setEndTimeType={setEndTimeType}
              isSpecificTime={isSpecificTime}
              onChangeSpecificTime={onChangeSpecificTime}
              isScreenTakeOverChecked={isScreenTakeOverChecked}
              setIsScreenTakeOverChecked={setIsScreenTakeOverChecked}
            />
          );
        default: break;
      }
    } else {
      switch (step) {
        case 0:
          return (
            <Info
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              adType={adType}
              adUrl={adUrl}
              postId={postId}
              onChangedAdUrl={(e) => setAdUrl(e)}
              onChangedPostId={(e) => setPostId(e)}
            />
          );
        case 1:
          return (
            <Media
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              postId={postId}
              adId={match.params.id}
              images={adImages}
              adVideo={adVideo}
              adType={adType}
              onChangedImages={(e) => setAdImages(e)}
              onChangedVideo={(e) => setAdVideo(e)}
            />
          );
        case 2:
          return (
            <GeographicTarget
              adId={match.params.id}
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
            />
          );
        case 3:
          return (
            <Dates
              activeStep={activeStep}
              onActiveStepChanged={(e) => setActiveStep(e)}
              dateFrom={dateFrom}
              dateTill={dateTill}
              onChangedDateFrom={(e) => setDateFrom(e)}
              onChangedDateTill={(e) => setDateTill(e)}
              onUpdate={handleUpdate}
              startTimeHours={startTimeHours}
              startTimeMins={startTimeMins}
              startTimeType={startTimeType}
              endTimeHours={endTimeHours}
              endTimeMins={endTimeMins}
              endTimeType={endTimeType}
              setStartTimeHours={setStartTimeHours}
              setStartTimeMins={setStartTimeMins}
              setStartTimeType={setStartTimeType}
              setEndTimeHours={setEndTimeHours}
              setEndTimeMins={setEndTimeMins}
              setEndTimeType={setEndTimeType}
              isSpecificTime={isSpecificTime}
              onChangeSpecificTime={onChangeSpecificTime}
              isScreenTakeOverChecked={isScreenTakeOverChecked}
              setIsScreenTakeOverChecked={setIsScreenTakeOverChecked}
            />
          );
        default:
      }
    }
  }

  const handleUpdate = async (e) => {
    try {
      e.preventDefault();

      const timeZone = '00+08';
      const dailyStartTime = convertTime12to24(`${startTimeHours}:${startTimeMins} ${startTimeType}`);
      const dailyEndTime = convertTime12to24(`${endTimeHours}:${endTimeMins} ${endTimeType}`);

      if (isSpecificTime) {
        if (IsEmpty(dateFrom) && IsEmpty(dateTill)) {
          return toast.error('Current Ad, does not have start and end date');
        }

        if (dailyStartTime === 'Invalid date') {
          return toast.error('Daily start time, is not valid');
        } else if (dailyEndTime === 'Invalid date') {
          return toast.error('Daily end time, is not valid');
        }
        // Parse the times using Moment.js and format them as HH:mm
        const startTime = moment(dailyStartTime, 'HH:mm');
        const endTime = moment(dailyEndTime, 'HH:mm');

        if (endTime.isSameOrBefore(startTime)) {
          return toast.error(`Daily end time is same or earlier than Daily start time`);
        }
      }

      const payload = {
        url:              adUrl,
        postId,
        pageId,
        dateFrom:         new Date(dateFrom).setHours(0, 0, 0, 0),
        dateTill:         new Date(dateTill).setHours(23, 59, 59, 999),
        dailyStartTime:   dailyStartTime === 'Invalid date' ? null : `${dailyStartTime}:${timeZone}`,
        dailyEndTime:     dailyEndTime === 'Invalid date' ? null : `${dailyEndTime}:${timeZone}`,
        isScreenTakeOver: isScreenTakeOverChecked,
      };

      /**
       * If "IsScreenTakeOver" then validate for overlapping of date or time with existingAd
       */
      if (isScreenTakeOverChecked) {
        const takeOverAds = await apiService.getAds({ isScreenTakeOver: true });

        // Check if current ad has date and time
        const currentAd = {
          id:             match.params.id,
          dateFrom:       moment(payload.dateFrom).format('YYYY-MM-DD'), // convert to SG-Date
          dateTill:       moment(payload.dateTill).format('YYYY-MM-DD'),
          dailyStartTime: payload.dailyStartTime,
          dailyEndTime:   payload.dailyEndTime,
        };

        for (const takeOverAd of takeOverAds) {
          const existingAd = {
            id:             takeOverAd.id,
            dateFrom:       takeOverAd.dateFrom,
            dateTill:       takeOverAd.dateTill,
            dailyStartTime: takeOverAd.dailyStartTime,
            dailyEndTime:   takeOverAd.dailyEndTime,
          };

          /**
         * 1) ExistingAd is not equal to CurrentAd, meaning it's a different takeover ad
         * 2) Check date range overlap
         * 3) Check 24-hour time period overlap
         */
          if (
          Number(existingAd.id) !== Number(currentAd.id)
          && IsDateRangeOverlap(currentAd.dateFrom, currentAd.dateTill, existingAd.dateFrom, existingAd.dateTill)
          && (
            (IsEmpty(existingAd.dailyStartTime)
            && IsEmpty(existingAd.dailyEndTime)) || (IsEmpty(currentAd.dailyStartTime) && IsEmpty(currentAd.dailyEndTime)))
          ) {
            return toast.error(`Date for currentAd is overlapping with existingAd of id "${existingAd.id}, because the time-period of ${isSpecificTime ? 'existingAd' : 'currentAd'} is 24-hours"`);
          }

          /**
         * 1) ExistingAd is not equal to CurrentAd, meaning it's a different takeover ad
         * 2) Check date range overlap
         * 3-4) Check time period overlap, only if we have currentAd 'dailyStartTime' & 'dailyEndTime' not 'NULL'
         */
          if (
          Number(existingAd.id) !== Number(currentAd.id)
          && IsDateRangeOverlap(currentAd.dateFrom, currentAd.dateTill, existingAd.dateFrom, existingAd.dateTill)
          && (!IsEmpty(currentAd.dailyStartTime) && !IsEmpty(currentAd.dailyEndTime))
          && IsTimePeriodOverlap(currentAd.dailyStartTime, currentAd.dailyEndTime, existingAd.dailyStartTime, existingAd.dailyEndTime)
          ) {
            return toast.error(`Time period for currentAd is overlapping with existingAd of id "${existingAd.id}"`);
          }
        }
      }

      const res = await updateAd(match.params.id, payload);
      if (res) {
        toast.success('Advertisement updated.');
        setGoBack(true);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  /* Overlay Scrollbar on Div instead of Sharing (Avoid Screen Squeeze) */
  const element = document.querySelector('div');
  element.style.setProperty('overflow-y', 'overlay');

  if (goBack) {
    return (<Redirect to="/admin/advertisement" />);
  }
  return !loaded ? <>Loading...</> : (
    <>
      <div className="row">
        <div className="col-sm-12">
          <div className="content-header" style={{ backgroundColor: '#fff', borderBottom: 'none' }}>
            <Grid item container direction="row" alignItems="center" xs={12} style={{ minHeight: 50 }}>
              <Grid
                item
                xs={2}
                md={1}
                container
                direction="row"
                alignItems="center"
                justify="center"
              >
                <Grid item container justify="flex-start">
                  <Typography variant="h1" className="d-flex align-items-center">
                    <div onClick={() => setGoBack(true)} className="d-flex align-items-center">
                      <Icon className="back">arrow_back</Icon>
                    </div>
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} md container direction="row">
                <Stepper classes={{ root: stepperClasses.root }} nonLinear activeStep={activeStep} alternativeLabel>
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepButton
                        onClick={() => setActiveStep(index)}
                        completed={completed[index]}
                      >
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>
          </div>
        </div>
        <div className="col-lg-12 row">
          <div className="col-xs-12 col-md-0 col-lg-1" />
          <div className="col-xs-12 col-md-12 col-lg-10">
            {getStepContent(activeStep)}
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, { getAdById, updateAd })(Advertisement);

Advertisement.propTypes = {
  match:     PropTypes.shape(Object).isRequired,
  getAdById: PropTypes.func.isRequired,
  updateAd:  PropTypes.func.isRequired,
};
